import { FaChevronRight } from "react-icons/fa"
import React, { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import sanityClient from "../sanityClient"
import { IoSearchOutline } from "react-icons/io5"
import Breadcrumbs from "../components/support/Breadcrumbs"
import useLogging from "../hooks/useLogging"
function highlightText(text, search) {
	if (!search) return text // Return original text if no search input
	const regex = new RegExp(`(${search})`, "gi") // Create a regex to match the search term
	const parts = text.split(regex) // Split the text by the search term
	return parts.map(
		(part, index) =>
			part.toLowerCase() === search.toLowerCase() ? (
				<span key={index} className="bg-yellow-300 inline">
					{part}
				</span>
			) : (
				part
			) // Highlight matching part
	)
}

export default function AllPosts({ sidebarOpen }) {
	const [allPostsData, setAllPosts] = useState([])
	const [searchInput, setSearchInput] = useState("")
	const logging = useLogging()
	useEffect(() => {
		sanityClient
			.fetch(
				`*[_type == "post"]{
          title,
          slug,
          mainImage{
            asset->{
              _id,
              url
            }
          },
          author->{
            name,
            image{
              asset->{
                _id,
                url
              }
            }
          },
          publishedAt,
          body,
          tags,
          "categories": categories[]->title
        }`
			)
			.then(data => {
				setAllPosts(data)
			})
			.catch(error => {
				logging.error("Error fetching posts:", error)
			})
		// `logging` is a fresh object each render (useLogging returns inline object); mount-only fetch.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const filteredPosts = allPostsData.filter(article =>
		article.title.toLowerCase().includes(searchInput.toLowerCase())
	)

	const groupedPosts = filteredPosts.reduce((acc, post) => {
		const category =
			post.categories && post.categories.length > 1 ? "General" : post.categories[0]

		if (!acc[category]) {
			acc[category] = []
		}
		acc[category].push(post)
		return acc
	}, {})

	const sortedGroupedPosts = {
		General: (groupedPosts.General || []).sort((a, b) => {
			const numA = parseInt(a.title.match(/\d+/)?.[0]) || Infinity
			const numB = parseInt(b.title.match(/\d+/)?.[0]) || Infinity
			return numA - numB || a.title.localeCompare(b.title)
		}),
		...Object.fromEntries(
			Object.entries(groupedPosts)
				.filter(([key]) => key !== "General")
				.map(([key, posts]) => [
					key,
					posts.sort((a, b) => {
						const numA = parseInt(a.title.match(/\d+/)?.[0]) || Infinity
						const numB = parseInt(b.title.match(/\d+/)?.[0]) || Infinity
						return numA - numB || a.title.localeCompare(b.title)
					})
				])
		)
	}

	const handleSearchChange = event => {
		setSearchInput(event.target.value)
	}

	return (
		<div className={`${sidebarOpen ? "pl-64" : "pl-8"}`}>
			<div className="my-5 flex h-auto sm:h-16 flex-col sm:flex-row items-start sm:items-center justify-between px-4 sm:px-6 lg:px-8 rounded-md">
				<h1 className="font-bold text-4xl font-manrope text-custom-blue mb-2 sm:mb-0 flex items-center">
					<span role="img" aria-label="books">
						📚
					</span>{" "}
					Support Pages
				</h1>
			</div>

			<div className="px-4 sm:px-6 lg:px-8">
				<Breadcrumbs
					className="font-medium text-lg font-manrope mb-2 text-gray-700"
					items={[{ label: "Support", link: "/support" }, { label: "Articles" }]}
				/>

				<p className="font-medium text-lg font-manrope mt-4 mb-2 text-gray-700">
					We're here to help with all your Oncare questions!
				</p>

				<div className="text-gray-500">{filteredPosts.length} articles</div>
			</div>

			<main className="py-10">
				<div className="px-4 sm:px-6 lg:px-8">
					{/* Search Bar */}
					<div className="mb-10 mx-auto">
						<div className="relative w-full flex items-center">
							<IoSearchOutline className="absolute left-3 text-gray-400" />
							<input
								type="text"
								placeholder="Search Support..."
								className="py-6 px-4 w-full border border-transparent rounded-md pl-8 text-sm h-8 bg-gray-100 text-gray-600 placeholder-gray-500 outline-none focus:outline-none focus:ring-0 focus:border-transparent"
								value={searchInput}
								onChange={handleSearchChange}
							/>
						</div>
					</div>

					{/* Posts Section */}
					{sortedGroupedPosts &&
						Object.entries(sortedGroupedPosts).map(([category, posts]) => (
							<div key={category} className="mb-8">
								<div className="bg-white shadow rounded-lg p-6">
									<h2 className="text-2xl font-bold text-gray-800">{category}</h2>
									<hr className="my-4 border-gray-200" />{" "}
									{/* Divider between title and posts */}
									<ul className="space-y-4">
										{posts.map(post => (
											<Link
												to={`/support/articles/${post.slug.current}`}
												key={post.slug.current}
												className="text-lg font-medium text-black block rounded-lg"
											>
												<li className="flex items-center justify-between p-4 rounded-lg hover:bg-blue-100 hover:text-customBlue transition-colors duration-200">
													<span className="flex-grow">
														{highlightText(post.title, searchInput)}
													</span>
													<FaChevronRight className="text-sm text-customBlue ml-2" />
												</li>
											</Link>
										))}
									</ul>
								</div>
							</div>
						))}
				</div>
			</main>
		</div>
	)
}
